
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { supabase } from '../../../lib/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: string;
  featured_image: string;
  created_at: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*, profiles(first_name, last_name)')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();

      if (error) {
        console.error('Error fetching post:', error);
        setPost(null);
        return;
      }

      if (!data) {
        console.log('No post found with slug:', slug);
        setPost(null);
        return;
      }

      setPost(data);

      // Fetch related posts
      if (data.category) {
        const { data: related } = await supabase
          .from('blog_posts')
          .select('*, profiles(first_name, last_name)')
          .eq('category', data.category)
          .eq('status', 'published')
          .neq('id', data.id)
          .limit(3);
        
        setRelatedPosts(related || []);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribing(true);

    try {
      const { error } = await supabase.from('newsletter_subscribers').insert({
        email,
        source: 'blog_article'
      });

      if (error) throw error;

      setSubscribeSuccess(true);
      setEmail('');
      setTimeout(() => setSubscribeSuccess(false), 3000);
    } catch (error) {
      console.error('Error subscribing:', error);
      alert('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const openAIAssistant = () => {
    const assistantButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (assistantButton) {
      assistantButton.click();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white/95">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white/95">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-search-line text-4xl text-red-500"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">
              Sorry, we couldn't find the article you're looking for. It may have been moved or deleted.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/blog')}
                className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Blog
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line mr-2"></i>
                Go Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={scrolled} />
      
      {/* Article Header */}
      <article className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1 max-w-4xl">
              <div className="mb-6">
                <Link to="/blog" className="inline-flex items-center text-gray-600 hover:text-[#0A381F] transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-arrow-left-line text-xl"></i>
                  </div>
                  Back to Blog
                </Link>
              </div>

              <div className="mb-6">
                <span className="bg-[#D4AF37] text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {post.category}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-[#0A381F] mb-6 font-serif">
                {post.title}
              </h1>

              <div className="flex items-center gap-4 text-gray-600 mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0A381F] text-white rounded-full font-semibold">
                    {post.profiles?.first_name?.[0]}{post.profiles?.last_name?.[0]}
                  </div>
                  <span className="font-semibold">{post.profiles?.first_name} {post.profiles?.last_name}</span>
                </div>
                <span>•</span>
                <span>{new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>

              {post.featured_image && (
                <div className="relative w-full h-96 rounded-2xl overflow-hidden mb-12">
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              )}

              {/* Article Content */}
              <div className="prose prose-lg max-w-none mb-12">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap font-serif">
                  {post.content}
                </div>
              </div>

              {/* AI Assistant CTA Box */}
              <div className="bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl p-8 text-white mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-2-fill text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">Have Questions About This Topic?</h3>
                    <p className="text-white/80 mb-4">Our AI Relocation Expert can provide personalized answers about your specific situation.</p>
                    <button
                      onClick={openAIAssistant}
                      className="bg-[#D4AF37] hover:bg-[#C19B2F] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                    >
                      <i className="ri-chat-voice-fill text-xl"></i>
                      Ask the Expert Now
                    </button>
                  </div>
                </div>
              </div>

              {/* Newsletter CTA */}
              <div className="bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl p-8 text-white mb-12">
                <h3 className="text-2xl font-bold mb-3">Get More Expert Insights</h3>
                <p className="text-white/80 mb-6">Join thousands of Americans successfully relocating to Ireland</p>
                
                {subscribeSuccess ? (
                  <div className="bg-green-500 text-white px-4 py-3 rounded-lg text-center font-semibold">
                    ✓ Successfully subscribed!
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="flex gap-3">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    />
                    <button
                      type="submit"
                      disabled={subscribing}
                      className="bg-[#D4AF37] hover:bg-[#C19B2F] text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {subscribing ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </form>
                )}
              </div>

              {/* Related Articles */}
              {relatedPosts.length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold text-[#0A381F] mb-8">Related Articles</h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.slug}`}
                        className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer"
                      >
                        <div className="relative w-full h-48">
                          <img
                            src={relatedPost.featured_image || 'https://public.readdy.ai/ai/img_res/34b8bcb2631cc1d3fd88be358abeb654.jpg'}
                            alt={relatedPost.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-bold text-[#0A381F] mb-2 line-clamp-2">{relatedPost.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2">{relatedPost.excerpt}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Recommended Services */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Recommended Services */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-bold text-[#0A381F] mb-4 flex items-center gap-2">
                    <i className="ri-star-fill text-[#D4AF37]"></i>
                    Recommended Services
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Wise Money Transfer */}
                    <a
                      href="https://wise.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="block p-4 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#0A381F] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-exchange-dollar-line text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#0A381F] mb-1">Wise</h4>
                          <p className="text-xs text-gray-600 mb-2">Save up to 8x on international transfers</p>
                          <span className="text-xs font-semibold text-[#D4AF37]">Get €75 Bonus →</span>
                        </div>
                      </div>
                    </a>

                    {/* Revolut Banking */}
                    <a
                      href="https://revolut.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="block p-4 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#0A381F] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-bank-card-line text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#0A381F] mb-1">Revolut</h4>
                          <p className="text-xs text-gray-600 mb-2">Digital banking for expats</p>
                          <span className="text-xs font-semibold text-[#D4AF37]">Open Free Account →</span>
                        </div>
                      </div>
                    </a>

                    {/* Booking.com */}
                    <a
                      href="https://www.booking.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="block p-4 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#0A381F] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-hotel-bed-line text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#0A381F] mb-1">Booking.com</h4>
                          <p className="text-xs text-gray-600 mb-2">Find your temporary home</p>
                          <span className="text-xs font-semibold text-[#D4AF37]">Search Stays →</span>
                        </div>
                      </div>
                    </a>

                    {/* VHI Healthcare */}
                    <a
                      href="https://www.vhi.ie"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="block p-4 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#0A381F] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-heart-pulse-line text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#0A381F] mb-1">VHI Healthcare</h4>
                          <p className="text-xs text-gray-600 mb-2">Ireland's leading health insurer</p>
                          <span className="text-xs font-semibold text-[#D4AF37]">Get Quote →</span>
                        </div>
                      </div>
                    </a>

                    {/* Greenback Tax */}
                    <a
                      href="https://www.greenbacktaxservices.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="block p-4 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#0A381F] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-text-line text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#0A381F] mb-1">Greenback Tax</h4>
                          <p className="text-xs text-gray-600 mb-2">US expat tax specialists</p>
                          <span className="text-xs font-semibold text-[#D4AF37]">Free Consultation →</span>
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Link
                      to="/directory"
                      className="text-sm text-[#0A381F] hover:text-[#D4AF37] font-semibold flex items-center gap-1 cursor-pointer"
                    >
                      View All Services
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-bold text-[#0A381F] mb-4">Helpful Tools</h3>
                  <div className="space-y-3">
                    <Link
                      to="/tools/cost-calculator"
                      className="flex items-center gap-3 text-gray-700 hover:text-[#0A381F] transition-colors cursor-pointer"
                    >
                      <i className="ri-calculator-line text-[#D4AF37]"></i>
                      <span className="text-sm font-semibold">Cost Calculator</span>
                    </Link>
                    <Link
                      to="/tools/visa-quiz"
                      className="flex items-center gap-3 text-gray-700 hover:text-[#0A381F] transition-colors cursor-pointer"
                    >
                      <i className="ri-passport-line text-[#D4AF37]"></i>
                      <span className="text-sm font-semibold">Visa Quiz</span>
                    </Link>
                    <Link
                      to="/tools/salary-converter"
                      className="flex items-center gap-3 text-gray-700 hover:text-[#0A381F] transition-colors cursor-pointer"
                    >
                      <i className="ri-money-dollar-circle-line text-[#D4AF37]"></i>
                      <span className="text-sm font-semibold">Salary Converter</span>
                    </Link>
                    <Link
                      to="/community"
                      className="flex items-center gap-3 text-gray-700 hover:text-[#0A381F] transition-colors cursor-pointer"
                    >
                      <i className="ri-group-line text-[#D4AF37]"></i>
                      <span className="text-sm font-semibold">Join Community</span>
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
